import { DatePicker } from "@/ds/custom/DatePicker";
import { Modal, ModalBody, ModalDescription, ModalFooter, ModalHeader, ModalTitle } from "@/ds/custom/Modal";
import { NumericInput } from "@/ds/custom/NumericInput";
import { Selector } from "@/ds/custom/Selector";
import { Button } from "@/ds/shadcn/components/ui/button";
import { Label } from "@/ds/shadcn/components/ui/label";
import { Spinner } from "@/ds/shadcn/components/ui/spinner";
import { useTransactionCreate } from "@/features/collections/api/useTransactionCreate";
import { TransactionActionOptions, type TransactionAction } from "@/features/collections/TransactionAction";
import { SmallProductDisplay } from "@/features/common/components/SmallProductDisplay";
import { ErrorDisplay } from "@/lib/common/ErrorDisplay";
import { useNotifyMutationSuccess } from "@/lib/common/useNotifyMutationSuccess";
import type { Money } from "@/lib/domain/Money";
import dayjs from "dayjs";
import { ChevronDownIcon } from "lucide-react";
import { useEffect, useState, type FC } from "react";

export interface ModalCreateTransactionVariantInformation {
	readonly variantId: string;
	readonly productName: string;
	readonly imageUrl: string;
	readonly fcId: string;
	readonly marketValue: Money;
}

export interface ModalCreateTransactionProps {
	collectionId: string;
	variant: ModalCreateTransactionVariantInformation | null;
	onClose: () => void;
}

export const ModalCreateTransaction: FC<ModalCreateTransactionProps> = props => {
	const {
		collectionId,
		variant,
		onClose
	} = props;

	const [quantity, setQuantity] = useState(1);
	const [action, setAction] = useState<TransactionAction>("Purchase");
	const [unitAmount, setUnitAmount] = useState(0);
	const [date, setDate] = useState(dayjs().toDate());

	const transactionCreate = useTransactionCreate();
	useNotifyMutationSuccess(transactionCreate, "Transaction Created", "Your transaction was created successfully.");

	useEffect(() => {
		if (!variant) {
			return;
		}

		setQuantity(1);
		setAction("Purchase");
		setUnitAmount(variant.marketValue.value);
		setDate(dayjs().toDate());
		transactionCreate.reset();
	}, [variant]);

	useEffect(() => {
		if (!transactionCreate.isSuccess) {
			return;
		}

		onClose();
	}, [transactionCreate.isSuccess]);

	const onConfirm = () => {
		if (!variant) {
			return;
		}

		transactionCreate.mutate({
			collectionId,
			requestModel: {
				variantId: variant.variantId,
				occurredDate: dayjs(date).format("YYYY-MM-DD"),
				action,
				quantity,
				unitAmount: {
					currency: variant.marketValue.currency,
					value: unitAmount
				}
			}
		});
	};

	const isLoading = transactionCreate.isPending;

	return (
		<Modal open={!!variant} onOpenChange={open => !open && onClose()} preventCloseOnInteractOutside={isLoading}>
			<ModalHeader>
				<ModalTitle>Record Transaction</ModalTitle>
				<ModalDescription>Specify the details of the transaction you would like to record for this product.</ModalDescription>
			</ModalHeader>
			<ModalBody>
				{
					variant &&
					<div className="flex flex-col gap-4">
						<SmallProductDisplay productName={variant.productName} fcId={variant.fcId} imageUrl={variant.imageUrl} />

						<div className="flex flex-col gap-2">
							<Label>Quantity</Label>
							<NumericInput
								value={quantity}
								onChange={setQuantity}
								maxDecimalPlaces={0}
								minValue={1}
								maxValue={100000} />
						</div>

						<div className="flex flex-col gap-2">
							<Label>Action</Label>
							<Selector
								className="w-full"
								placeholder="Action"
								options={TransactionActionOptions}
								value={action}
								onChange={setAction} />
						</div>

						<div className="flex flex-col gap-2">
							<Label>Unit Amount ({variant.marketValue.currency})</Label>
							<NumericInput
								value={unitAmount}
								onChange={setUnitAmount}
								minValue={0}
								maxValue={500000} />
						</div>

						<div className="flex flex-col gap-2">
							<Label>Date</Label>
							<DatePicker value={date} onDateSelected={setDate}>
								<Button variant="outline" className="w-full">
									<div className="flex w-full items-center">
										<div className="flex-1 text-left">
											{dayjs(date).format("DD MMM YYYY")}
										</div>

										<ChevronDownIcon className="ml-auto" />
									</div>
								</Button>
							</DatePicker>
						</div>

						<ErrorDisplay error={transactionCreate.error} />
					</div>
				}
			</ModalBody>
			<ModalFooter>
				<Button variant="outline" onClick={onClose} disabled={isLoading}>
					Cancel
				</Button>
				<Button onClick={onConfirm} disabled={isLoading}>
					{isLoading ? <Spinner /> : <>Record</>}
				</Button>
			</ModalFooter>
		</Modal>
	);
};
